import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DropStatus } from '@sss/types';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { deleteDrop, getDropStatus } from '../lib/dropsApi';
import { readFragment } from '../lib/secretLinks';
import { SecretsApiError } from '../lib/secretsApi';
import { formatBytes } from '../lib/tools';
import ui from '../styles/ui.module.css';
import styles from './RevealPage.module.css';

/**
 * /drop-status/:id#<senderToken> — a stored drop's status for its sender
 * (docs/adr/0015): downloads started and finished, when it goes, what the
 * pass is charged, and "delete now". As /status, the token stays in the
 * fragment and reaches the API only in an Authorization header.
 */

type State = { step: 'loading' } | { step: 'incomplete' } | { step: 'unknown' } | { step: 'failed' } | { step: 'loaded'; status: DropStatus };

export default function DropStatusPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [token] = useState(() => readFragment(window.location.hash));
  const [state, setState] = useState<State>({ step: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [announce, setAnnounce] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  usePageTitle(t('pageTitle.dropStatus'));

  const load = useCallback(async () => {
    if (!token) return setState({ step: 'incomplete' });
    try {
      setState({ step: 'loaded', status: await getDropStatus(id, token) });
    } catch (err) {
      setState({ step: err instanceof SecretsApiError && err.status === 404 ? 'unknown' : 'failed' });
    }
  }, [id, token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);

  async function onDelete() {
    if (!token) return;
    setDeleting(true);
    await deleteDrop(id, token).catch(() => undefined);
    setDeleting(false);
    setConfirming(false);
    await load();
    heading.current?.focus();
  }

  async function onRefresh() {
    setAnnounce('');
    await load();
    setAnnounce(t('status.refreshed'));
  }

  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const titleKey = (status: DropStatus) => (status.state === 'ended' ? `dropStatus.ended.${status.endReason ?? 'deleted'}` : `dropStatus.state.${status.state}`);

  return (
    <main className={styles.page}>
      <div className={styles.stack}>
        <p className={ui.eyebrow}>{t('dropStatus.eyebrow')}</p>
        {state.step === 'loading' && <p role="status">{t('status.loading')}</p>}
        {state.step === 'incomplete' && <h1 className={styles.title}>{t('status.incomplete')}</h1>}
        {state.step === 'unknown' && (
          <>
            <h1 className={styles.title}>{t('dropStatus.unknown.title')}</h1>
            <p className={styles.lead}>{t('dropStatus.unknown.body')}</p>
          </>
        )}
        {state.step === 'failed' && <h1 className={styles.title}>{t('status.failed')}</h1>}
        {state.step === 'loaded' && (
          <>
            <h1 className={styles.title} ref={heading} tabIndex={-1}>
              {t(titleKey(state.status))}
            </h1>
            <dl className={secretStyles.facts}>
              <dt>{t('dropStatus.downloads')}</dt>
              <dd>{t('dropStatus.downloadsValue', { used: state.status.maxDownloads - state.status.downloadsLeft, max: state.status.maxDownloads })}</dd>
              <dt>{t('status.created')}</dt>
              <dd>{when(state.status.createdAt)}</dd>
              {state.status.endsAt && state.status.state !== 'ended' && (
                <>
                  <dt>{t('dropStatus.ends')}</dt>
                  <dd>{when(state.status.endsAt)}</dd>
                </>
              )}
              {state.status.endedAt && (
                <>
                  <dt>{t('status.ended')}</dt>
                  <dd>{when(state.status.endedAt)}</dd>
                </>
              )}
              <dt>{state.status.bytesCharged === null ? t('dropStatus.held') : t('dropStatus.charged')}</dt>
              <dd>{formatBytes(state.status.bytesCharged ?? state.status.bytesHeld)}</dd>
            </dl>
            {state.status.downloads.length > 0 && (
              <section>
                <h2 className={ui.eyebrow}>{t('dropStatus.downloadsTitle')}</h2>
                <ol data-testid="drop-downloads">
                  {state.status.downloads.map((d, i) => (
                    <li key={`${d.startedAt}-${i}`}>
                      {d.doneAt ? t('dropStatus.downloadDone', { started: when(d.startedAt), done: when(d.doneAt) }) : t('dropStatus.downloadStarted', { started: when(d.startedAt) })}
                    </li>
                  ))}
                </ol>
              </section>
            )}
            {state.status.state !== 'ended' &&
              (confirming ? (
                <div className={secretStyles.panel} role="group" aria-label={t('status.confirmLabel')}>
                  <p className={secretStyles.notice}>{t('dropStatus.confirm')}</p>
                  <div className={secretStyles.actions}>
                    <button type="button" className={secretStyles.dangerButton} onClick={onDelete} disabled={deleting} ref={confirmButton}>
                      {t('status.deleteNow')}
                    </button>
                    <button
                      type="button"
                      className={ui.buttonSecondary}
                      onClick={() => {
                        setConfirming(false);
                        requestAnimationFrame(() => deleteButton.current?.focus());
                      }}
                    >
                      {t('status.keep')}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className={secretStyles.dangerButton} onClick={() => setConfirming(true)} ref={deleteButton}>
                  {t('status.delete')}
                </button>
              ))}
            <button type="button" className={ui.buttonSecondary} onClick={() => void onRefresh()}>
              {t('status.refresh')}
            </button>
            <p role="status" className="visually-hidden">
              {announce}
            </p>
            <p className={ui.hint}>{t('dropStatus.retention')}</p>
          </>
        )}
      </div>
    </main>
  );
}
